import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  RefreshCw, LogOut, Copy, Check, Inbox as InboxIcon,
  Mail, Clock, KeyRound, ChevronLeft, ExternalLink, Link2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import ClaudeLogo from "@/components/ClaudeLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function cleanSubject(s) {
  if (!s) return "";
  return s.split(" | ")[0].trim();
}

function fmtCountdown(s) {
  if (s == null) return "";
  const m = Math.floor(s / 60);
  const sec = String(s % 60).padStart(2, "0");
  return `${m}:${sec}`;
}

function LinkBlock({ url, expiresIn, isExpired }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    Promise.resolve(navigator.clipboard?.writeText(url)).then(() => {
      setCopied(true);
      toast.success("Sign-in link copied");
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => toast.error("Couldn't copy — select the link and copy manually"));
  };
  return (
    <div className="mb-6" data-testid="link-block">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-[#78716C] font-medium">
          Sign-in link
        </div>
        {expiresIn != null && !isExpired && (
          <span
            data-testid="link-countdown"
            className={`text-xs font-mono px-2 py-0.5 rounded-md ${
              expiresIn <= 60 ? "bg-[#D32F2F]/10 text-[#D32F2F]" : "bg-[#2E7D32]/10 text-[#2E7D32]"
            }`}
          >
            Expires in {fmtCountdown(expiresIn)}
          </span>
        )}
        {isExpired && (
          <span data-testid="link-expired" className="text-xs px-2 py-0.5 rounded-md bg-[#B45309]/10 text-[#B45309]">
            Expired
          </span>
        )}
      </div>
      <div className={`rounded-xl border border-[#E7E5E4] p-4 ${isExpired ? "bg-[#FAFAF9] opacity-70" : "bg-[#F5F5F4]"}`}>
        <div
          data-testid="signin-link-url"
          className="font-mono text-xs text-[#57534E] break-all mb-4 flex items-start gap-2"
        >
          <Link2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#CC785C]" />
          <span>{url}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="signin-link-open"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#CC785C] hover:bg-[#B8654D] text-white text-sm font-medium px-4 py-2 transition-colors duration-200"
          >
            <ExternalLink className="h-4 w-4" /> Open sign-in link
          </a>
          <button
            onClick={copy}
            data-testid="signin-link-copy"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#E7E5E4] bg-white hover:bg-[#FAFAF9] text-[#57534E] text-sm font-medium px-4 py-2 transition-colors duration-200"
          >
            {copied ? <Check className="h-4 w-4 text-[#2E7D32]" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
      </div>
      {isExpired && (
        <p className="mt-2 text-xs text-[#B45309]">
          This link has expired. Trigger a new sign-in on Claude and it will appear here automatically.
        </p>
      )}
    </div>
  );
}

function WaitingPanel() {
  return (
    <div className="flex flex-col items-center justify-center h-[60vh] text-center px-6" data-testid="waiting-panel">
      <div className="relative mb-5">
        <span className="absolute inset-0 rounded-full bg-[#CC785C]/20 animate-ping" />
        <span className="relative flex h-12 w-12 items-center justify-center rounded-full bg-[#CC785C]/10">
          <Mail className="h-6 w-6 text-[#CC785C]" />
        </span>
      </div>
      <h3 className="font-serif text-xl text-[#141413] mb-2">Waiting for your sign-in link…</h3>
      <p className="text-sm text-[#57534E] max-w-sm">
        Trigger a login on Claude.ai and your fresh sign-in link will appear here automatically — no need to refresh.
      </p>
      <p className="text-xs text-[#A8A29E] mt-3">Checking every few seconds</p>
    </div>
  );
}

function OtpBlock({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    Promise.resolve(navigator.clipboard?.writeText(code)).then(() => {
      setCopied(true);
      toast.success("Code copied");
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => toast.error("Couldn't copy — select the code manually"));
  };
  return (
    <div className="mb-6" data-testid="otp-block">
      <div className="text-xs uppercase tracking-wider text-[#78716C] mb-2 font-medium">
        Verification code
      </div>
      <button
        onClick={copy}
        data-testid="otp-copy-button"
        className="group w-full flex items-center justify-between gap-4 rounded-xl border border-[#E7E5E4] bg-[#F5F5F4] p-5 transition-colors duration-200 hover:border-[#CC785C]/50"
      >
        <span
          data-testid="otp-code"
          className="font-mono text-3xl sm:text-4xl tracking-[0.25em] text-[#141413] font-medium"
        >
          {code}
        </span>
        <span className="flex items-center gap-1.5 text-sm text-[#57534E] group-hover:text-[#CC785C] transition-colors duration-200">
          {copied ? <Check className="h-4 w-4 text-[#2E7D32]" /> : <Copy className="h-4 w-4" />}
          {copied ? "Copied" : "Copy"}
        </span>
      </button>
    </div>
  );
}

const LINK_TTL = 600;        // Claude sign-in links expire ~10 min
const FRESH_WINDOW = 1800;   // still show a link (as "expired") if within 30 min

export default function Inbox({ session, onLogout }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const inFlight = useRef(false);

  const fetchInbox = useCallback(async ({ spinner = false, announce = false } = {}) => {
    if (inFlight.current) return;
    inFlight.current = true;
    if (spinner) setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/access/inbox`, {
        key: session.key,
        email: session.email,
      });
      const list = res.data.emails || [];
      setEmails(list);
      setFetchedAt(Date.now());
      setSelected((prev) => {
        if (prev) {
          const match = list.find((e) => e.id === prev.id);
          return match || list[0] || null;
        }
        return list[0] || null;
      });
      if (announce) toast.success("Inbox refreshed");
    } catch (err) {
      if (err?.response?.status === 403 || err?.response?.status === 404) {
        toast.error(err.response.data.detail || "Session expired.");
        onLogout();
        return;
      }
      setError(err?.response?.data?.detail || "Could not load your inbox.");
    } finally {
      inFlight.current = false;
      if (spinner) setLoading(false);
    }
  }, [session, onLogout]);

  // Initial load
  useEffect(() => { fetchInbox({ spinner: true }); }, [fetchInbox]);

  // Live auto-fetch every 10s while the tab is visible
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchInbox({});
    }, 10000);
    return () => clearInterval(id);
  }, [fetchInbox]);

  // 1s ticker for the live countdown
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const secsSinceFetch = fetchedAt ? (now - fetchedAt) / 1000 : 0;
  const currentAge = selected?.age_seconds != null ? selected.age_seconds + secsSinceFetch : null;
  const expiresIn = currentAge != null ? Math.max(0, Math.round(LINK_TTL - currentAge)) : null;
  const isExpired = currentAge != null && currentAge > LINK_TTL;
  const showLink = !!selected?.link && currentAge != null && currentAge <= FRESH_WINDOW;
  const hasEmail = !!selected;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-[#E7E5E4] bg-[#F9F8F6]/85 backdrop-blur-md">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <ClaudeLogo className="h-7 w-7" />
            <span className="font-serif text-xl font-medium tracking-tight text-[#141413]">
              Claude Access
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:flex items-center gap-1.5 text-xs text-[#78716C] mr-1" data-testid="live-indicator">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#2E7D32] opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#2E7D32]" />
              </span>
              Live
            </span>
            <Button
              onClick={() => fetchInbox({ spinner: true, announce: true })}
              data-testid="refresh-button"
              variant="ghost"
              className="h-9 px-3 text-[#57534E] hover:text-[#141413] hover:bg-[#F5F5F4]"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 sm:mr-2 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              onClick={onLogout}
              data-testid="logout-button"
              variant="ghost"
              className="h-9 px-3 text-[#57534E] hover:text-[#D32F2F] hover:bg-[#F5F5F4]"
            >
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-6">
        <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-1">
          <div className="flex items-center gap-2 text-sm text-[#57534E]">
            <Mail className="h-4 w-4 text-[#CC785C]" />
            <span data-testid="active-email" className="font-medium text-[#141413]">{session.email}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#78716C]">
            <KeyRound className="h-3.5 w-3.5" />
            <span className="font-mono text-xs">{session.key}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* List */}
          <div className={`lg:col-span-1 ${hasEmail ? "hidden lg:block" : "block"}`}>
            <div className="rounded-2xl border border-[#E7E5E4] bg-white overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
                <h2 className="font-serif text-lg text-[#141413]">Inbox</h2>
                <span className="text-xs text-[#78716C]">{loading ? "Loading…" : `${emails.length} message${emails.length !== 1 ? "s" : ""}`}</span>
              </div>
              <div className="max-h-[70vh] overflow-y-auto no-scrollbar" data-testid="email-list">
                {loading ? (
                  <div className="p-5 space-y-4">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="space-y-2">
                        <Skeleton className="h-4 w-3/4 bg-[#F5F5F4]" />
                        <Skeleton className="h-3 w-1/2 bg-[#F5F5F4]" />
                      </div>
                    ))}
                  </div>
                ) : error ? (
                  <div className="p-8 text-center">
                    <p className="text-sm text-[#D32F2F] mb-3">{error}</p>
                    <Button onClick={() => fetchInbox({ spinner: true })} variant="outline" className="border-[#E7E5E4]">Retry</Button>
                  </div>
                ) : emails.length === 0 ? (
                  <div className="p-10 text-center" data-testid="empty-state">
                    <InboxIcon className="h-10 w-10 mx-auto text-[#D6D3D1] mb-3" />
                    <p className="text-sm text-[#57534E]">Waiting for your sign-in link…</p>
                    <p className="text-xs text-[#A8A29E] mt-1">It will appear here automatically.</p>
                  </div>
                ) : (
                  emails.map((em, i) => (
                    <motion.button
                      key={em.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.04 }}
                      onClick={() => setSelected(em)}
                      data-testid={`email-item-${i}`}
                      className={`w-full text-left px-5 py-4 border-b border-[#F0EFED] transition-colors duration-200 ${
                        selected?.id === em.id ? "bg-[#F5F5F4]" : "hover:bg-[#FAFAF9]"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm font-medium text-[#141413] line-clamp-1">
                          {em.from_name || em.from_addr || "Anthropic"}
                        </span>
                        <span className="shrink-0 text-xs text-[#A8A29E] flex items-center gap-1">
                          <Clock className="h-3 w-3" />{timeAgo(em.date)}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-[#57534E] line-clamp-1">{cleanSubject(em.subject)}</div>
                      {em.link ? (
                        <span className="mt-2 inline-flex items-center gap-1 rounded-md bg-[#CC785C]/10 px-2 py-0.5 text-xs font-medium text-[#B8654D]">
                          <Link2 className="h-3 w-3" /> Sign-in link
                        </span>
                      ) : em.otp ? (
                        <span className="mt-2 inline-block rounded-md bg-[#CC785C]/10 px-2 py-0.5 text-xs font-mono font-medium text-[#B8654D]">
                          Code: {em.otp}
                        </span>
                      ) : null}
                    </motion.button>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Detail */}
          <div className={`lg:col-span-2 ${hasEmail ? "block" : "hidden lg:block"}`}>
            <div className="rounded-2xl border border-[#E7E5E4] bg-white min-h-[60vh]">
              <AnimatePresence mode="wait">
                {selected ? (
                  <motion.div
                    key={selected.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="p-6 sm:p-8"
                    data-testid="email-detail"
                  >
                    <button
                      onClick={() => setSelected(null)}
                      className="lg:hidden mb-4 flex items-center gap-1 text-sm text-[#57534E]"
                    >
                      <ChevronLeft className="h-4 w-4" /> Back
                    </button>
                    <h2 className="font-serif text-2xl sm:text-3xl text-[#141413] leading-snug mb-3">
                      {cleanSubject(selected.subject)}
                    </h2>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[#78716C] mb-6 pb-6 border-b border-[#F0EFED]">
                      <span className="text-[#57534E]">{selected.from_name || selected.from_addr || "Anthropic"}</span>
                      <span className="text-[#A8A29E]">·</span>
                      <span>{selected.date ? new Date(selected.date).toLocaleString() : ""}</span>
                    </div>

                    {showLink ? (
                      <LinkBlock url={selected.link} expiresIn={expiresIn} isExpired={isExpired} />
                    ) : selected.link ? (
                      <div className="mb-6 rounded-xl border border-[#B45309]/30 bg-[#B45309]/5 p-4 text-sm text-[#B45309]" data-testid="stale-notice">
                        The last sign-in link is too old to use. A new one will appear here automatically once you trigger a login on Claude.
                      </div>
                    ) : selected.otp ? (
                      <OtpBlock code={selected.otp} />
                    ) : (
                      <div
                        data-testid="email-body"
                        className="whitespace-pre-wrap break-words text-[15px] leading-relaxed text-[#3f3d3a] font-sans"
                      >
                        {selected.body || "(This message has no readable text content.)"}
                      </div>
                    )}
                  </motion.div>
                ) : (
                  <WaitingPanel />
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
